import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { AiConfig } from "../../models/theme.model";

/**
 * The AI configuration form, shared by the AI tab components (filters / scoring / score bands /
 * content configuration). Maps 1:1 onto the backend `AiConfigPayload`: one group because it's one
 * payload object, every field optional. The parent `theme-form` owns it and each tab component
 * reads/mutates only its own subset via `formControlName`.
 */
export type AiGroup = FormGroup<{
    tagCategoryLabelA: FormControl<string>;
    tagCategoryA: FormControl<string>;
    tagCategoryLabelB: FormControl<string>;
    tagCategoryB: FormControl<string>;
    tagCategoryLabelC: FormControl<string>;
    tagCategoryC: FormControl<string>;
    tagCategoryLabelD: FormControl<string>;
    tagCategoryD: FormControl<string>;
    tagCategoryLabelE: FormControl<string>;
    tagCategoryE: FormControl<string>;
    tagCategoryLabelF: FormControl<string>;
    tagCategoryF: FormControl<string>;
    spamCriteria: FormControl<string>;
    transcriptionVocabularyCriteria: FormControl<string>;
    audioEnergyLabel: FormControl<string>;
    audioEnergyScoreCriteria: FormControl<string>;
    score0to49Criteria: FormControl<string>;
    score50to59Criteria: FormControl<string>;
    score60to69Criteria: FormControl<string>;
    score70to79Criteria: FormControl<string>;
    score80to89Criteria: FormControl<string>;
    score90to100Criteria: FormControl<string>;
}>;

/** The six filter slots (T-5). */
export const TAG_SLOTS = [
    { slot: "A", labelControl: "tagCategoryLabelA", criteriaControl: "tagCategoryA" },
    { slot: "B", labelControl: "tagCategoryLabelB", criteriaControl: "tagCategoryB" },
    { slot: "C", labelControl: "tagCategoryLabelC", criteriaControl: "tagCategoryC" },
    { slot: "D", labelControl: "tagCategoryLabelD", criteriaControl: "tagCategoryD" },
    { slot: "E", labelControl: "tagCategoryLabelE", criteriaControl: "tagCategoryE" },
    { slot: "F", labelControl: "tagCategoryLabelF", criteriaControl: "tagCategoryF" }
] as const;

/**
 * The six score bands (T-7). Labels are the **backend** boundaries — authoritative per the
 * 2026-08-12 BE-8 decision (PRD/design were out of sync and are being corrected to match). `tier`
 * mirrors the producer dashboard's `scoreTier()` buckets (CREATORS-29817) so the band pill uses the
 * same bad→elite color ramp (0 = bad, 100 = elite/good).
 */
export const SCORE_BANDS = [
    { label: "0–49", control: "score0to49Criteria", tier: "bad" },
    { label: "50–59", control: "score50to59Criteria", tier: "low" },
    { label: "60–69", control: "score60to69Criteria", tier: "mid" },
    { label: "70–79", control: "score70to79Criteria", tier: "good" },
    { label: "80–89", control: "score80to89Criteria", tier: "high" },
    { label: "90–100", control: "score90to100Criteria", tier: "elite" }
] as const;

/** Builds the AI group, optionally pre-filled from a loaded `aiConfig` (edit mode). */
export function createAiGroup(fb: FormBuilder, config?: AiConfig | null): AiGroup {
    const control = (value: string | undefined) => fb.nonNullable.control(value ?? "");
    return fb.nonNullable.group({
        tagCategoryLabelA: control(config?.tagCategoryLabelA),
        tagCategoryA: control(config?.tagCategoryA),
        tagCategoryLabelB: control(config?.tagCategoryLabelB),
        tagCategoryB: control(config?.tagCategoryB),
        tagCategoryLabelC: control(config?.tagCategoryLabelC),
        tagCategoryC: control(config?.tagCategoryC),
        tagCategoryLabelD: control(config?.tagCategoryLabelD),
        tagCategoryD: control(config?.tagCategoryD),
        tagCategoryLabelE: control(config?.tagCategoryLabelE),
        tagCategoryE: control(config?.tagCategoryE),
        tagCategoryLabelF: control(config?.tagCategoryLabelF),
        tagCategoryF: control(config?.tagCategoryF),
        spamCriteria: control(config?.spamCriteria),
        transcriptionVocabularyCriteria: control(config?.transcriptionVocabularyCriteria),
        audioEnergyLabel: control(config?.audioEnergyLabel),
        audioEnergyScoreCriteria: control(config?.audioEnergyScoreCriteria),
        score0to49Criteria: control(config?.score0to49Criteria),
        score50to59Criteria: control(config?.score50to59Criteria),
        score60to69Criteria: control(config?.score60to69Criteria),
        score70to79Criteria: control(config?.score70to79Criteria),
        score80to89Criteria: control(config?.score80to89Criteria),
        score90to100Criteria: control(config?.score90to100Criteria)
    });
}

/**
 * Builds an `AiConfig` from the group, trimmed, keeping only the filled fields — so a partially
 * filled AI section saves what the user typed. Returns `undefined` when nothing is filled, leaving
 * `aiConfig` out of the payload rather than sending an empty object.
 */
export function buildAiConfig(group: AiGroup): AiConfig | undefined {
    const raw = group.getRawValue();
    const config: AiConfig = {};
    for (const [key, value] of Object.entries(raw)) {
        const trimmed = value.trim();
        if (trimmed.length > 0) config[key as keyof AiConfig] = trimmed;
    }
    return Object.keys(config).length ? config : undefined;
}
